<template>
  <div>
    <TopButtonsComponent
      :jobCreatorId="creatorId"
      v-on:backToJobs="$emit('backToJobs', 'Jobs')"
      v-on:backToPostJob="$emit('backToPostJob', 'Post Job')"
    />
    <div class="edit-job-container" v-if="!loading">
      <div class="left-content">

        <div class="form-field">
          <h3>Job Title <span class="required">*</span></h3>
          <input
            name="title"
            type="text"
            v-model="job.title"
            v-on:change="validateText"
            required
          />
          <p v-if="error.titleError" class="error-text">
            Please enter a valid input
          </p>
        </div>

        <div class="form-field">
          <h3>Description <span class="required">*</span></h3>
          <textarea
            type="text"
            name="jobDescription"
            v-model="job.jobDescription"
            v-on:change="validateText"
            required
          ></textarea>
          <p v-if="error.jobDescriptionError" class="error-text">
            Please enter at least 20 characters
          </p>
        </div>

        <div class="form-field">
          <h3>Job Cover Photo <span class="file-size">(max 2MB - JPG or PNG)</span></h3>
          <div
            v-if="!viewImage && !job.jobCoverPhotoUrl"
            class="upload-image"
            @click="$refs.myFile.click()"
          >
            <span>
              <img src="../../assets/images/upload-logo.png" alt="upload icon"/>
            </span>
            <span>
              <p>Upload Cover Image</p>
            </span>
            <input
              type="file"
              placeholder="Upload CoverImage"
              ref="myFile"
              @change="selectedFile"
              hidden
            />
            <h6>Add a cover image that will be seen by applicants</h6>
          </div>
          <div
            v-if="viewImage || job.jobCoverPhotoUrl"
            class="upload-image"
            v-on:click="resetImage"
          >
            <span>
              <img class="preview-image" :src="viewImage ? viewImage : job.jobCoverPhotoUrl" alt="Preview image" />
            </span>
            <span>
              <p>Remove Image</p>
            </span>
          </div>

          <div v-if="imageError">
            <p class="error-text">
              Please select an image no more than the specified file size and type.
            </p>
            <span v-on:click="clearImageError" class="clear-image-error">
              **Click to continue editing without uploading a cover image.
            </span>
          </div>
        </div>

        <div class="form-field">
          <h3>Specialization <span class="required">*</span></h3>
          <select
            name="category"
            v-model="job.category"
            v-on:change="validateText"
            required
          >
            <option
              v-for="(item, index) in categoryList"
              :key="index"
              :value="item.value"
            >
              {{ item.title }}
            </option>
          </select>
          <p v-if="error.categoryError" class="error-text">
            Please enter a valid input
          </p>
        </div>

        <div class="form-field">
          <h3>Experience Level <span class="required">*</span></h3>
          <select
            name="experienceLevel"
            v-model="job.experienceLevel"
            v-on:change="validateText"
            required
          >
            <option
              v-for="(item, index) in experienceLevelList"
              :key="index"
              :value="item.value"
            >
              {{ item.title }}
            </option>
          </select>
          <p v-if="error.experienceLevelError" class="error-text">
            Please enter a valid input
          </p>
        </div>

        <div class="form-field">
          <h3>Job Type <span class="required">*</span></h3>
          <select
            name="employmentType"
            v-model="job.employmentType"
            v-on:change="validateText"
            required
          >
            <option
              v-for="(item, index) in employmentTypeList"
              :key="index"
              :value="item.value"
            >
              {{ item.title }}
            </option>
          </select>
          <p v-if="error.employmentTypeError" class="error-text">
            Please enter a valid input
          </p>
        </div>

      </div><!-- end of left-content div -->

      <div class="right-content">

        <div class="form-field">
          <h3>City <span class="required">*</span></h3>
          <input
            name="city"
            type="text"
            v-model="job.city"
            v-on:change="validateText"
            placeholder=""
            required
          />
          <p v-if="error.cityError" class="error-text">
            Please enter a valid input
          </p>
        </div>

        <div class="form-field">
          <h3>State/Province <span class="required">*</span></h3>
          <input
            name="state"
            type="text"
            v-model="job.state"
            v-on:change="validateText"
            placeholder=""
            required
          />
          <p v-if="error.stateError" class="error-text">
            Please enter a valid input
          </p>
        </div>

        <div class="form-field">
          <h3>Job Deadline <span class="required">*</span></h3>
          <input
            name="jobDeadline"
            type="date"
            v-model="job.jobDeadline"
            v-on:change="validateText"
            required
          />
          <p v-if="error.jobDeadlineError" class="error-text">
            Please enter a valid input
          </p>
        </div>

        <div class="form-field" v-if="showCompanyName">
          <h3>Company Name <span class="required">*</span></h3>
          <input
            name="companyName"
            type="text"
            v-model="job.companyName"
            v-on:change="validateText"
            required
          />
          <p v-if="companyNameError" class="error-text">
            Please enter a valid input
          </p>
        </div>

        <div class="form-field question">
          <h3>Question 1 <span class="hint">* (should be a Yes/No question)</span></h3>
          <input
            name="applicantQuestionOne"
            type="text"
            v-model="job.applicantQuestionOne"
            v-on:change="validateText"
            placeholder="e.g. Do you meet the job minimum requirements?"
          />
          <p v-if="error.applicantQuestionOneError" class="error-text">
            Please enter a minimum of five characters
          </p>
        </div>

        <div class="form-field question">
          <h3>Question 2 <span class="hint">* (should be a Yes/No question)</span></h3>
          <input
            name="applicantQuestionTwo"
            type="text"
            v-model="job.applicantQuestionTwo"
            v-on:change="validateText"
            placeholder="e.g. Do you have the required job experience?"
          />
          <p v-if="error.applicantQuestionTwoError" class="error-text">
            Please enter a minimum of five characters
          </p>
        </div>

        <div class="form-field question">
          <h3>Question 3 <span class="hint">* (should be a Yes/No question)</span></h3>
          <input
            name="applicantQuestionThree"
            type="text"
            v-model="job.applicantQuestionThree"
            v-on:change="validateText"
            placeholder="e.g. Are you available for immediate employment?"
          />
          <p v-if="error.applicantQuestionThreeError" class="error-text">
            Please enter a minimum of five characters
          </p>
        </div>

        <div class="form-field video-cv-guide">
          <h3>Video Questions(Guide)</h3>
          <textarea
            name="videoGuide"
            v-model="job.videoCvGuide"
            v-on:change="validateText"
            rows="4"
            placeholder="Please specify any question or guideline you want answered or followed on candidate video CV (if any). For multiple comments, press the enter key"
          />
          <p v-if="error.videoGuideError" class="error-text">
            Please enter a minimum of five characters
          </p>
        </div>

        <div class="submit-button-container">
          <button :disabled="disable" @click="handleSubmit">Update Job</button>
        </div>

      </div><!-- end of right-content div -->

    </div><!-- end of edit-job-container div -->

    <!-- Loader -->
    <div class="loading-text" v-if="loading">
      <p>Please wait</p>
      <div v-bind:class="{ loading: loading }"></div>
    </div>

    <!-- Modal component for Successful Job Post -->
    <div v-if="showSuccessModal">
      <SuccessModal
        v-on:closeSuccessModal="showSuccessModal = false"
        v-bind:successImage="successImage"
        v-bind:successTitle="'Success!'"
        v-bind:successMessage="'Job has successfully been updated'"
      />
    </div>

  </div>
</template>

<script>
import TopButtonsComponent from '../JobDetail/TopButtonsComponent.vue';
import SuccessModal from '../Modals/SuccessModal.vue';
import successImage from '../../assets/images/success-logo.png';
export default {
  name: 'EditJob',
  props: ['jobCreatorId'],
  components: {
    TopButtonsComponent,
    SuccessModal
  },
  data() {
    return {
      job: {},
      jobId: null,
      error: {
        titleError: false,
        jobDescriptionError: false,
        categoryError: false,
        experienceLevelError: false,
        employmentTypeError: false,
        cityError: false,
        stateError: false,
        jobDeadlineError: false,
        applicantQuestionOneError: false,
        applicantQuestionTwoError: false,
        applicantQuestionThreeError: false,
        videoGuideError: false,
      },

      imageError: false,
      companyNameError: false,

      showCompanyName: false,
      experienceLevelList: [
        { title: 'Advanced', value: 'advanced' },
        { title: 'Senior', value: 'senior' },
        { title: 'Intermediate (Mid-level)', value: 'intermediate' },
        { title: 'Junior', value: 'junior' },
        { title: 'Beginner', value: 'beginner' },
        { title: 'Intern', value: 'intern' },
      ],
      categoryList: [
        { title: 'Technology', value: 'Technology' },
        { title: 'Product Design', value: 'Product Design' },
        { title: 'Product Management', value: 'Product Management' },
        { title: 'Project Management', value: 'Project Management' },
        { title: 'Administration', value: 'Administration' },
        { title: 'Education', value: 'Education' },
        { title: 'Operations', value: 'Operations' },
        { title: 'Sales', value: 'Sales' },
        { title: 'Marketing', value: 'Marketing' },
        { title: 'Customer Service', value: 'Customer Service' },
        { title: 'Human Resources', value: 'Human Resources' },
        { title: 'Creative & Design', value: 'Creative and Design' },
        { title: 'Media & Communications', value: 'Media and Communications' },
        { title: 'Health & Wellness', value: 'Health and Wellness' },
        { title: 'Hospitality & Tourism', value: 'Hospitality and Tourism' },
        { title: 'Arts & Entertainment', value: 'Arts and Entertainment' },
        { title: 'Science & Research', value: 'Science and Research' },
        { title: 'Agriculture', value: 'Agriculture' },
        { title: 'Finance', value: 'Finance' },
        { title: 'Supply Chain', value: 'Supply Chain' },
        { title: 'Engineering', value: 'Engineering' },
        {
          title: 'Building & Construction',
          value: 'Building and Construction',
        },
        {
          title: 'Logistics & Transportation',
          value: 'Logistics and Transportation',
        },
        { title: 'Legal', value: 'Legal' },

        { title: 'Energy & Utilities', value: 'Energy and Utilities' },
        { title: 'Telecommunications', value: 'Telecommunications' },
        {
          title: 'Petroleum & Petrochemicals',
          value: 'Petroleum and Petrochemicals',
        },
        {
          title: 'Furniture & Interior Design',
          value: 'Furniture and Interior Design',
        },
        {
          title: 'Manufacturing',
          value: 'Manufacturing'
        },
        {
          title: 'Healthcare',
          value: 'Healthcare'
        },
        {
          title: 'Others',
          value: 'Others'
        },
      ],
      employmentTypeList: [
        { title: 'Full Time', value: 'Full Time' },
        { title: 'Full Time - Remote', value: 'Full Time - Remote' },
        { title: 'Full Time - Hybrid', value: 'Full Time - Hybrid' },
        { title: 'Part Time', value: 'Part Time' },
        { title: 'Part Time - Remote', value: 'Part Time - Remote' },
        { title: 'Part Time - Hybrid', value: 'Part Time - Hybrid' },
        { title: 'Contract', value: 'Contract' },
        { title: 'Contract - Remote', value: 'Contract - Remote' },
        { title: 'Contract - Hybrid', value: 'Contract - Hybrid' },
        { title: 'Internship', value: 'Internship' },
      ],

      viewImage: null,
      disable: false,
      loading: false,

      creatorId: this.jobCreatorId,
      showSuccessModal: false,
      imageUploadError:
      'We accept only jpeg, png files of not more than 2MB.',
    };
  },
  methods: {
    selectedFile(event) {
      if ((event.target.files[0].type === 'image/jpeg' || event.target.files[0].type === 'image/png') &&
        event.target.files[0].size <= 2000000 ) {
        this.job.jobCoverPhotoUrl = event.target.files[0];
        this.viewImage = URL.createObjectURL(this.job.jobCoverPhotoUrl);
        // Set a job property that informs that the job cover photo has changed
        this.job['isCoverPhotoChanged'] = true;
        this.imageError = false;
      } else {
        this.imageError = true;
        this.$toastr.error(this.imageUploadError, 'Wrong File Type/Size:');
      }
    },
    resetImage() {
      this.job.jobCoverPhotoUrl = null;
      this.viewImage = null;
    },
    clearImageError () {
      this.imageError = false;
      this.disable = false; //reset button disabled attribute
    },
    handleSubmit() {
      if (
        this.error.titleError ||
          this.error.jobDescriptionError ||
          this.error.categoryError ||
          this.error.experienceLevelError ||
          this.error.employmentTypeError ||
          this.error.city ||
          this.error.state ||
          this.error.jobDeadline ||
          this.error.applicantQuestionOneError ||
          this.error.applicantQuestionTwoError ||
          this.error.applicantQuestionThreeError ||
          this.error.videoGuideError
      ) {
        Object.keys(this.job).forEach((field) => {
          if ((field !== 'applicantQuestionOne' && field !== 'applicantQuestionTwo' && field !== 'applicantQuestionThree' && field !== 'videoGuide') &&
          !this.job[field]) {
            this.error[`${field}Error`] = true;
          }
        });
      } else if (this.imageError === true) {
        // Set disabled attribute to the Create Job button
        this.disable = true;
      } else {
        if (this.showCompanyName &&
            (this.companyNameError || !this.job.companyName))
        {
          this.companyNameError = true;
        } else {
          this.loading = true;

          // modify Job object to ensure that optional fields are not flagged on the backend when submitted empty
          this.job.applicantQuestionOne !== '' && this.job.applicantQuestionOne !== null
          ? this.job.applicantQuestionOne
          : delete this.job.applicantQuestionOne;

          this.job.applicantQuestionTwo !== '' && this.job.applicantQuestionTwo !== null
          ? this.job.applicantQuestionTwo
          : delete this.job.applicantQuestionTwo;

          this.job.applicantQuestionThree !== '' && this.job.applicantQuestionThree !== null
          ? this.job.applicantQuestionThree
          : delete this.job.applicantQuestionThree;

          this.job.videoCvGuide !== '' && this.job.videoCvGuide !== null
          ? this.job.videoCvGuide
          : delete this.job.videoCvGuide;

          this.job.companyName !== '' && this.job.companyName !== null
          ? this.job.companyName
          : delete this.job.companyName;

          this.$store
            .dispatch('updateJob', this.job)
            .then((res) => {
              if (res.response) {
                this.loading = false;

                this.successImage = successImage;
                this.showSuccessModal = true;

                // To-Do: Redirect to job detail page when the job detail page dynamic binding to jobId is completed.
                // remove action dispatch when the above is done.
                this.$store.dispatch('fetchJob', this.jobId);
                // this.$router.push(`/user/jobs/${this.jobId}/view`);

                setTimeout(() => {
                  this.showSuccessModal = false;
                }, 5000);

              } else if (res.error) {
                this.$toastr.error(res.error ? res.error.error : res.error.errorMessage, 'Failed');
                this.loading = false;
              }
            })
            .catch((err) => {
              this.loading = false;
              this.$toastr.error(
                err ? err : 'Error occured while attempting to edit job',
                'Failed'
              );
            });
        }
      }
    },
    validateText(e) {
      this.disable = false;
      let fieldName = e.target.name;
      let fieldValue = e.target.value;

      switch (fieldName) {
        case 'title':
          if (fieldValue.trim() === '' || fieldValue.trim().length < 3) {
            this.error.titleError = true;
          } else {
            this.error.titleError = false;
          }
          break;

        case 'jobDescription':
          if (fieldValue.trim() === '' || fieldValue.trim().length < 20) {
            this.error.jobDescriptionError = true;
          } else {
            this.error.jobDescriptionError = false;
          }
          break;

        case 'category':
          if (fieldValue.trim() === '') {
            this.error.categoryError = true;
          } else {
            this.error.categoryError = false;
          }
          break;

        case 'experienceLevel':
          if (fieldValue.trim() === '') {
            this.error.experienceLevelError = true;
          } else {
            this.error.experienceLevelError = false;
          }
          break;

        case 'employmentType':
          if (fieldValue.trim() === '') {
            this.error.employmentTypeError = true;
          } else {
            this.error.employmentTypeError = false;
          }
          break;

        case 'city':
          if (fieldValue.trim() === '' || fieldValue.trim().length < 3) {
            this.error.cityError = true;
          } else {
            this.error.cityError = false;
          }
          break;

        case 'state':
          if (fieldValue.trim() === '' || fieldValue.trim().length < 3) {
            this.error.stateError = true;
          } else {
            this.error.stateError = false;
          }
          break;

        case 'jobDeadline':
          if (fieldValue.trim() === '') {
            this.error.jobDeadlineError = true;
          } else {
            this.error.jobDeadlineError = false;
          }
          break;

        case 'companyName':
          if (fieldValue.trim() === '' || fieldValue.trim().length < 3) {
            this.companyNameError = true;
          } else {
            this.companyNameError = false;
          }
          break;

        case 'applicantQuestionOne':
          if (fieldValue.trim() !== '' && fieldValue.trim().length < 5) {
            this.error.applicantQuestionOneError = true;
          } else {
            this.error.applicantQuestionOneError = false;
          }
          break;

        case 'applicantQuestionTwo':
          if (fieldValue.trim() !== '' && fieldValue.trim().length < 5) {
            this.error.applicantQuestionTwoError = true;
          } else {
            this.error.applicantQuestionTwoError = false;
          }
          break;

        case 'applicantQuestionThree':
          if (fieldValue.trim() !== '' && fieldValue.trim().length < 5) {
            this.error.applicantQuestionThreeError = true;
          } else {
            this.error.applicantQuestionThreeError = false;
          }
          break;

        case 'videoGuide':
          if (fieldValue.trim() !== '' && fieldValue.trim().length < 5) {
            this.error.videoGuideError = true;
          } else {
            this.error.videoGuideError = false;
          }
          break;
      }
    },
  },
  async beforeMount() {
    // TO-DO:
    // Revisit this funcitonality after application is staged to include ability to name company
    // as Anonymous at the DB level

    this.loading = true;

    this.jobId = this.$router.history.current.params.jobId;

    this.$store.state.userData.userType !== 'business'
    ? (this.showCompanyName = true)
    : (this.showCompanyName = false);

    await this.$store.dispatch('fetchJob', this.jobId)
    .then((res) => {
      this.job = res.response.data;
      this.loading = false;
    })
    .catch((err) => {
      this.loading = false;
      this.$toastr.error(err, 'Failed');
    });
  },
};
</script>
<style scoped>
  @keyframes buttonLoadingSpinner {
    from {
      transform: rotate(0turn);
    }
    to {
      transform: rotate(1turn);
    }
  }

  .loading-text {
    position: relative;
    display: flex;
    align-items: center;
    margin-top: 50px;
    color: white;
    justify-content: center;
  }
  .loading::after {
    content: "";
    position: absolute;
    width: 100px;
    height: 100px;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    border: 4px solid transparent;
    border-top-color: var(--white);
    border-right-color: var(--white);
    border-radius: 50%;
    animation: buttonLoadingSpinner 1s linear infinite;
  }
  .edit-job-container {
    background: var(--white);
    box-shadow: 0px 0px 4px 2px rgba(180, 180, 180, 0.15);
    border-radius: 15px;
    display: flex;
    flex-direction: row;
    padding: 30px;
    justify-content: space-between;
    min-height: 1050px;
  }

  .left-content,
  .right-content {
    width: 45%;
  }
  .right-content {
    padding-right: 20px;
  }
  .left-content {
    padding-left: 20px;
  }

  h3 {
    font-family: "montserrat";
    font-style: normal;
    font-weight: 600;
    font-size: 1rem;
    line-height: 1.25rem;
    color: var(--colorSecondary);
    padding-bottom: 16px;
  }
  select,
  input,
  textarea {
    width: 100%;
    max-width: 100%;
    height: 40px;
    box-shadow: 0px 0px 4px 2px rgba(39, 31, 31, 0.15);
    border-radius: 5px;
    outline: none;
    padding: 10px;
    font-family: montserrat;
    font-size: 1rem;
    color: rgba(51, 52, 54, 0.7);
    line-height: 1.25rem;
    /* identical to box height */
    -webkit-appearance: auto;
    color: rgba(51, 52, 54, 0.7);
  }
  .form-field {
    margin-bottom: 30px;
  }
  .upload-image,
  textarea {
    height: 130px;
    max-height: 120px;
    resize: none;
  }
  .upload-image {
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    border: 1px dashed #071a52;
    box-sizing: border-box;
    border-radius: 5px;
    padding: 15px;
    cursor: pointer;
  }
  .upload-image img {
    margin-bottom: 10px;
  }
  .upload-image p {
    font-family: 'montserrat';
    font-style: normal;
    font-weight: bold;
    font-size: 17px;
    line-height: 21px;
    color: var(--colorSecondary);
    margin-bottom: 8px;
    cursor: pointer;
  }
  .preview-image {
    width: 30px;
    height: 30px;
  }
  .upload-image h6 {
    font-family: 'montserrat';
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 21px;
    color: #828282;
  }
  .required,
  .error-text {
    color: tomato;
  }
  .hint {
    font-size: 12px;
    margin-left: 15px;
    color: #828282;
  }
  .error-text {
    margin-top: 15px;
  }
  .clear-image-error {
    cursor: pointer;
    font-size: 15px;
    text-decoration: underline;
    display: block;
    margin-top: 10px;
  }
  .submit-button-container {
    margin-top: 80px;
  }
  .submit-button-container button {
    background: var(--colorPrimary);
    color: var(--white);
    width: 188px;
    height: 53px;
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.15));
    border-radius: 5px;
    float: right;
    outline: none;
  }
  .question input::placeholder,
  .video-cv-guide textarea::placeholder {
    font-size: 13px;
  }

  /* RESPONSIVENESS */
  @media only screen and (max-width: 850px) {
    .edit-job-container {
      display: block;
      min-height: 1600px;
    }
    .left-content,
    .right-content {
      width: 100%;
    }
    .left-content {
      padding-left: unset;
    }
    .right-content {
      padding-right: unset;
    }
    .submit-button-container {
      margin-top: 40px;
    }
  }
  @media only screen and (max-width: 767px) {

  }
</style>
